<template>
  <div>
    <TopSection
      :title="sheet_name || $t('usages') | capitalize"
      :subTitle="file_name"
      :count=null
      :section="$t('playlists')|capitalize"
      :sub-section="$t('usages')|capitalize"
      backSection="playlist-files"
    >
      <template v-slot:title__after>
        <SelectedFilters class="ml-3" :table="$refs.UsagesTable" />
      </template>

      <template v-slot:filters>
        <div class="mr-3">
          <span
              v-if="file_name"
              v-b-tooltip.hover
              class="link-class"
              :title="$t('download')"
              @click="downloadUsageFile()"
          >
            <i class="far fa-download" />
            {{ $t('downloadFile') | capitalize }}
          </span>
        </div>
        <FilterModule
          :title="$t('filterSoundRecordings')|capitalize"
          :statuses="{
            [statusTypes.UNMATCHED]: $t('unmatched'),
            [statusTypes.UNMATCHED_NO_DUPLICATES]: $t('unmatched_no_duplicates'),
            [statusTypes.AUTOMATCHED]: $t('automatched'),
            [statusTypes.MANUALLY_MATCHED]: $t('manually_matched'),
            [statusTypes.AUTO_MANUALLY_MATCHED]: $t('auto_manually_matched'),
            [statusTypes.UNMATCHED_READY_FOR_DISTRIBUTION]: $t('unmatched_ready_for_distribution')
          }"
          :table="$refs.UsagesTable"
        />
      </template>
    </TopSection>

    <div class="mt-3">
      <!-- TABLE -->
      <v-server-table
        v-if="dataReady"
        ref="UsagesTable"
        :url="`/soundrecordingusages/`"
        :columns="columns"
        :options="options"
        @row-click="selectRow"
        @pagination="onPagination"
        @sorted="onSorted"
        @filter="onFilter"
        @loaded="onLoaded"
      >
        <!-- TITLE COLUMN -->
        <template v-slot:title="{ row }">
          <div class="table-header">
            {{ row.title | capitalize }}
          </div>
          <div class="mb-1">
            <div
              v-for="metadata in getMetadata(row,'title')"
              :key="metadata"
              class="table-meta mb-2"
            >
              {{ metadata }}
            </div>
          </div>
        </template>

        <!-- ARTIST COLUMN -->
        <template v-slot:artist="{ row }">
          <div class="table-header">
            {{ row.artist | capitalize }}
          </div>
          <div class="mb-1">
            <div
              v-for="metadata in getMetadata(row,'artist')"
              :key="metadata"
              class="table-meta mb-2"
            >
              {{ metadata }}
            </div>
          </div>
        </template>

        <!-- ISRC COLUMN -->
        <template v-slot:isrc="{ row }">
          <span class="medium">{{ row.isrc | upper }}</span>
        </template>

        <!-- ALBUM NAMES COLUMN -->
        <template v-slot:releases="{ row }">
          <div
            v-for="name in row.releases"
            v-bind:key="name"
            class="table-meta mb-2"
          >
            {{ name }}
          </div>
        </template>


        <!-- DURATION COLUMN-->
        <template v-slot:duration="{ row }">
          {{ row.duration | normalizeSeconds }}
        </template>

        <!-- COUNTRY OF RECORDING COLUMN-->
        <template v-slot:fixation_country="{ row }">
          {{ row.fixation_country_name }}
        </template>

        <!-- LANGUAGE OF PERF COLUMN-->
        <template v-slot:lang_of_perf="{ row }">
          {{ getMetadata(row,'lang_of_perf')[0] }}
        </template>

        <!-- RELEASE NAMES COLUMN-->
        <template v-slot:releaseNames="{ row }">
          <div
            v-for="metadata in getMetadata(row,'release')"
            :key="metadata"
            class="table-meta mb-2"
          >
            {{ metadata }}
          </div>
        </template>

        <!-- PLAYCOUNT COLUMN-->
        <template v-slot:play_count="{ row }">
          {{ row.play_count }}
        </template>

        <!-- AIRDATETIME COLUMN -->
        <template v-slot:airdatetime="{ row }">
          <div v-if="row.airdatetime">
            <div class="table-main-class">
              {{ row.airdatetime | transformDate("MMMM Do YYYY") }}
            </div>
            <div class="table-sub-class text-sm">
              {{ row.airdatetime | transformDate("h:mm a") }}
            </div>
          </div>
        </template>

        <!-- LICENSEE COLUMN-->
        <template v-slot:licensee="{ row }">
          {{ row.licensee }}
        </template>

        <!-- STATUS COLUMN-->
        <template v-slot:status="{ row }">
          <Status
            v-if="row.status == statusTypes.AUTOMATCHED"
            :text="$t('automatched') | capitalize"
            :icon="['fas', 'check-circle']"
            color="green"
          />
          <Status
            v-if="row.status == statusTypes.MANUALLY_MATCHED"
            :text="$t('manually_matched') | capitalize"
            :icon="['fas', 'check-circle']"
            color="green"
          />
          <Status
            v-if="row.status == statusTypes.AUTO_MANUALLY_MATCHED"
            :text="$t('auto_manually_matched') | capitalize"
            :icon="['fas', 'check-circle']"
            color="green"
          />
          <Status
            v-if="row.status == statusTypes.UNMATCHED"
            :text="$t('unmatched') | capitalize"
            :icon="['fas', 'exclamation-circle']"
            color="red"
          />
          <Status
            v-if="row.status == statusTypes.UNMATCHED_NO_DUPLICATES"
            :text="$t('unmatched_no_duplicates') | capitalize"
            :icon="['fas', 'exclamation-circle']"
            color="red"
          />
        </template>

        <!-- SOURCE COLUMN-->
        <template v-slot:source="{ row }">
          <router-link
            :to="{ name: 'usages', query: { source: row.source_id && row.source_id }}"
          >
            {{ row.source_id && "playlist" }}
          </router-link>
        </template>

        <!-- ACTIONS COLUMN -->
        <template v-slot:actions="{ row }">
          <div class="d-flex justify-content-end">
            <span
              v-if="!matched(row)"
              class="clickable table-sub-class text-sm pr-4"
            >
              <i
                :class="selectedSoundRecording.id===row.id ? 'fa fa-chevron-up':'fa fa-pencil'"
              />
            </span>
            <span
              v-else
              style="z-index:1000;"
              class="clickable table-sub-class text-sm pr-4"
              @click="navigateToSR(row.sound_recording.id)"
            >
              <i class="fas fa-eye" />
            </span>
          </div>
        </template>

        <!-- CHILD ROWS -->
        <template v-slot:child_row="props">
          <div v-if="props.row.status === statusTypes.UNMATCHED">
            <div class="d-flex justify-content-between py-4 pl-2">
              <div class="medium-big">
                {{ $t('proposedCandidates') | capitalize }}
              </div>
              <div class="d-flex">
                <button
                  v-b-modal="'recordingDetailsModal'"
                  class="button"
                >
                  <i class="table-sub-class text-sm far fa-music mr-2" />
                  {{ $t('createPlaceholder') | capitalize }}
                </button>

                <b-input-group class="ml-4 button search-group">
                  <b-input-group-prepend>
                    <i class="table-sub-class text-sm far fa-search mr-2" />
                  </b-input-group-prepend>
                  <b-form-input
                    class="search shadow-none"
                    v-model="props.row.search_text"
                    :placeholder="$t('search_candidates') | capitalize"
                    debounce="500"
                  />
                </b-input-group>
              </div>
            </div>
            <SelectRecordTable
              :id="props.row.id"
              type="usages"
              :search_text="props.row.search_text"
              @navigateToSR="navigateToSR"
              @selectedSuccess="recordSelected()"
              @closing="props.row.search_text=''"
            />
          </div>
          <div v-else-if="matched(props.row)">
            <div
              v-if="props.row.sound_recording && !loading"
              class="m-4"
            >
              <RecordingDetails
                :title="soundRecording.title | capitalize"
                :artist="soundRecording.artist | capitalize"
                :extras="soundRecording.extras "
                :duration="soundRecording.duration"
                :year="soundRecording.fixation_year"
                :type="soundRecording.type"
                :country-of-recording="soundRecording.first_publication_country"
                :country="soundRecording.first_producer_nationality"
                :isrc="soundRecording.isrc"
                :fixation-country="soundRecording.fixation_country "
                :eligible="soundRecording.eligible"
                @toggleEligible="toggleEligible"
              />
            </div>
            <div class="m-4">
              <TopSection :title="$t('claims')|capitalize" />
              <div>
                <ClaimsTable
                  ref="table"
                  :sound-recording="props.row.sound_recording"
                  :sound-recording-id="props.row.sound_recording.id"
                  @mapSoundRecording="mapSoundRecording"
                />
              </div>
            </div>
          </div>
        </template>
      </v-server-table>
    </div>
    <RecordingDetailsModal
      :key="selectedSoundRecording.id"
      ref="recordingDetailsModal"
      modal-type="usages"
      :sound-recording="selectedSoundRecording"
      @close="closeModal"
    />

    <!-- The target of the link is the iframe, given by its name -->
    <div style="display:none;">
      <iframe
        v-for="link in downloadLinks"
        :key="link"
        :name="link"
      >
        <a :target="link" :href="link" :id="link" download>{{ link }}</a>
      </iframe>
    </div>
  </div>
</template>

<style lang="scss" scoped >
input.search {
  border: 0 !important;
  background: transparent;
  width: 80%;
}

.search-group.input-group {
  width: inherit;

  .input-group-prepend {
    display: block;
  }
}

.search.form-control {
  padding: 0;
  height: inherit;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

textarea:focus,
input:focus {
  outline: none;
}

.blurred {
  filter: blur(2px);
}
</style>

<script>
import { capitalize, delay, get } from "lodash"
import {
  doRequest,
  responsePaginatedAdapter
} from "@/assets/libs/utils"
import { mapActions, mapGetters, mapMutations, mapState } from "vuex"
import RecordingDetailsModal from "../../Modules/RecordingDetailsModal.vue"
import { api } from "@/main"
import { listRouteMixin } from "@/utils/mixins"

export default {
  name: "UsagesPage",
  mixins: [listRouteMixin],
  components: {
    RecordingDetailsModal
  },

  beforeRouteLeave (to, from, next) {
    this.clearFilters()
    next()
  },

  created () {
    let selectedStatus = get(this.$route.query, "status", "")
    this.setStatusFilter(selectedStatus)
  },

  methods: {
    ...mapActions("ui", ["clearFilters"]),
    ...mapMutations("ui", ["setStatusFilter"]),

    addRecordingDetails () {
      this.recordingDetailsModalOpen = true
    },
    closeModal (modal) {
      const { UsagesTable } = this.$refs
      const page = this.$route.query.page
      this.$nextTick(() => {
        // Wrapped in $nextTick to ensure DOM is rendered before closing
        UsagesTable.setPage(page)

        modal.hide()
      })

      this.$refs.UsagesTable.toggleChildRow(
        this.selectedSoundRecording.id
      )
      this.selectedSoundRecording = {}

      UsagesTable.refresh()
    },
    getData () {
      this.$refs.UsagesTable.getData()
    },
    getMetadata (row, tag) {
      const extras = this.$options.filters.get(
        row,
        "sound_recording.extras",
        null
      )
      if (extras) {
        return extras.reduce((current, next) => {
          if (next.tag === tag) {
            current.push(next.value)
          }
          return current
        }, [])
      }
      return []
    },
    onFilter (search) {
      this.$router.replace({ query: { ...this.$route.query, fuzzy_search: search, page: 1 } })
    },
    onLoaded () {
      let selectedStatus = get(this.$route.query, "status", "")
      this.setStatusFilter(selectedStatus)
      this.$refs.UsagesTable.$refs.table.count = this.count

      if (this.$route.query.source) {
        this.columns = this.columns.filter(column => column !== "source")
      } else {
        if (!this.columns.includes("source")) this.columns.push("source")
      }
    },
    mapSoundRecording (soundRecording) {
      this.loading = false
      this.soundRecording = soundRecording
    },
    navigateToSR (id) {
      this.$router.push({
        name: "sound-recording-from-usages-detail",
        params: {
          id
        }
      })
    },
    matched (row) {
      return [this.statusTypes.MANUALLY_MATCHED, this.statusTypes.AUTOMATCHED, this.statusTypes.AUTO_MANUALLY_MATCHED].includes(row.status)
    },
    recordSelected () {
      const { UsagesTable } = this.$refs
      UsagesTable.toggleChildRow(this.selectedSoundRecording.id)
      this.selectedSoundRecording = {}
      UsagesTable.refresh()
    },
    toggleEligible () {
      api
        .put(`soundrecordings/${this.soundRecording.id}/eligibility/`, {
          eligible: !this.soundRecording.eligible
        })
        .then(() => {
          const { table } = this.$refs
          table.$refs.claimsTable.refresh()
        })
        .catch(err => { this.$toasted.global.toast(err) })
    },
    goBack () {
      this.$router.push({ name: "sound-recordings" })
    },
    clickOnDownLoadLink (downloadLink) {
      this.downloadLinks = [downloadLink]
      this.$nextTick(() => {
        const link = document.getElementById(downloadLink)
        link.click()
      })
    },
    downloadUsageFile () {
      api
        .get(`playlistfilesheet/${this.$route.query.source}/download/`)
        .then(res => {
          const downloadLink = res.data
          try {
            this.clickOnDownLoadLink(downloadLink)
          } catch (error) {
            this.$toasted.global.toast(error)
          }
        })
        .catch(err => {
          if (err.response && err.response.data) {
            err.response.data.forEach(msg => { this.$toasted.global.toast(msg) })
          } else {
            this.$toasted.global.toast(err)
          }
        })
    },
    selectRow ({ row }) {
      this.loading = true
      const { UsagesTable } = this.$refs
      UsagesTable.toggleChildRow(this.selectedSoundRecording.id)
      if (this.selectedSoundRecording.id === row.id) {
        this.selectedSoundRecording = {}
      } else {
        delay(UsagesTable.toggleChildRow, 50, row.id)
        this.selectedSoundRecording = row
      }
    }
  },
  computed: {
    ...mapState("soundsys", ["dataReady"]),
    ...mapGetters("soundsys", ["statusTypes"]),
    ...mapState("ui", ["statusFilter"]),
  },

  data () {
    return {
      loading: false,
      soundRecording: {},
      selectedSoundRecording: {},
      recordingDetailsModalOpen: false,
      matchedPercentage: "",
      sheet_name: "",
      file_name: "",
      created: "",
      count: 0,
      downloadLinks: [],
      columns: [
        "title",
        "artist",
        "isrc",
        "releases",
        "duration",
        "play_count",
        "airdatetime",
        "licensee",
        "status",
        "source",
        "actions"
      ],

      options: {
        rowClassCallback: row => {
          // If an item is selected
          if (Object.keys(this.selectedSoundRecording).length) {
            if (row.id !== this.selectedSoundRecording.id) {
              return "blurred"
            }
            if (row.id === this.selectedSoundRecording.id) {
              return "clickable mh70"
            }
          }

          return "clickable mh70"
        },
        sortable: ["airdatetime", "play_count"],
        filterable: true,
        headings: {
          title: capitalize(this.$t("title")),
          artist: capitalize(this.$t("artist")),
          isrc: capitalize(this.$t("isrc")),
          releases: capitalize(this.$t("releaseNames")),
          duration: capitalize(this.$t("duration")),
          play_count: capitalize(this.$t("play_count")),
          airdatetime: capitalize(this.$t("airdatetime")),
          licensee: capitalize(this.$t("licensee")),
          status: capitalize(this.$t("status")),
          source: capitalize(this.$t("source")),
          actions: ""
        },
        columnsClasses: {
          title: "width-medium",
          artist: "width-medium",
          isrc: "width-medium",
          duration: "width-small",
        },
        requestFunction (params) {
          const queryParams = { ...params, ...this.$route.query }
          this.query = get(queryParams, "fuzzy_search", "")
          this.page = Number(get(queryParams, "page", 1))
          if (this.page > 1000) this.page = 1
          return doRequest(this.url, queryParams)
        },
        responseAdapter: resp => {
          if (this.selectedSoundRecording.id) {
            this.$refs.UsagesTable.toggleChildRow(
              this.selectedSoundRecording.id
            )
            this.selectedSoundRecording = {}
          }
          if (resp && resp.data) {
            this.matchedPercentage = resp.data.matched
            this.sheet_name = resp.data.sheet_name
            this.file_name = resp.data?.playlist_file_url?.split("/").pop()
            this.created = resp.data.created
            this.count = Math.min(Number(resp.data.count), 10000)
            this.after_key = resp.data.after_key
          }
          return responsePaginatedAdapter(resp)
        }
      }
    }
  }
}
</script>
